import React, { ReactNode, useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { getInLocalStorage, setInLocalStorage } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import {
    useModal,
    useDetectionTracks,
    usePaymentPlanStatus,
} from '../../../hooks';
import { EnableDetectionReminderContent } from '.';
import { getLocalStorageKey } from './helpers';

const { GET_SESSION_CURRENT_USER } = queryStrings;

interface EnableDetectionReminderProps {
    children: ReactNode;
}

/**
 * Checks whether a user:
 * - has an active payment plan
 * - is not viewing the ManageDetections modal or the ManageStatementsModal
 * - has detection capacity
 * - has uploaded audio files
 * - has not enabled detection
 *
 * If all conditions are met, shows a modal that reminds the user to enable detection. The
 * modal is only shown once per userId, as tracked by a flag set in localStorage.
 *
 * @param props - see {@link EnableDetectionReminderProps}
 */
export const EnableDetectionReminder = ({
    children,
}: EnableDetectionReminderProps): JSX.Element => {
    const { add: addModal, hasOpenModalRef } = useModal();
    const { subscriptionSettings, isPublisherEdition } = usePaymentPlanStatus();
    const active = subscriptionSettings?.subscription_active ?? false;
    const {
        detectionStats: { detectionCapacity, detectionTracksCount, enabled },
    } = useDetectionTracks();

    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;

    useEffect(() => {
        if (
            isPublisherEdition === false &&
            userId &&
            active &&
            hasOpenModalRef.current === false
        ) {
            const localStorageKey = getLocalStorageKey(userId);

            const showedEnableDetectionReminder =
                getInLocalStorage(localStorageKey);

            if (
                !showedEnableDetectionReminder &&
                detectionTracksCount > 0 &&
                detectionCapacity !== null &&
                detectionCapacity > 0 &&
                enabled.nonDraftOnly !== null &&
                enabled.nonDraftOnly < 1
            ) {
                addModal({
                    Content: EnableDetectionReminderContent,
                });

                setInLocalStorage(localStorageKey, () => true);
            }
        }
    }, [
        active,
        detectionCapacity,
        detectionTracksCount,
        enabled.nonDraftOnly,
        isPublisherEdition,
        hasOpenModalRef.current,
        userId,
    ]);

    return <>{children}</>;
};
